import PropTypes from "prop-types";
import {
  FormControlLabel as MuiFormControlLabel,
  Checkbox as MuiCheckbox,
} from "@mui/material";
import { useTranslation } from "react-i18next";

/**
 * @callback CheckboxOnChange
 * @param {event} e The event source of the callback
 * @param {Function} setValue Formik's value setter for this Field
 */

/**
 * A custom component for use with Formik's Field component that also wraps
 * Material UI's Checkbox component so we can take advantage of both tools.
 *
 * @param {object} props
 * @param {object} props.field Formik's field value object -- { name, value, onChange, onBlur }
 * @param {object} props.form Formik's form object
 * @param {string} props.label A react-i18next translation file entry
 * @param {CheckboxOnChange} [props.onChange] Optional onChange callback to be executed
 *        This custom onChange will be passed the field setter & is expected to manage the field state.
 * @param {object} [props.styles] Any additional styles, passed to MUI's sx prop
 *
 * @see https://formik.org/docs/api/field#component
 * @see https://mui.com/material-ui/react-checkbox/
 */
const FormikCheckbox = ({
  field,
  form,
  label,
  onChange = null,
  styles = {},
  ...otherProps
}) => {
  const { t } = useTranslation();
  const { onChange: formikOnChange, ...fieldProps } = field;
  const changeHandler = onChange ?? formikOnChange;
  const customOnChange = !!onChange;

  return (
    <MuiFormControlLabel
      label={t(label)}
      control={
        <MuiCheckbox
          id={`formik-mui-checkbox-${field.name}`}
          onChange={(e) => {
            if (customOnChange) {
              // Get the field setter to inject into the custom onChange
              const { setValue } = form.getFieldHelpers(field.name);
              changeHandler(e, setValue);
            } else {
              formikOnChange(e);
            }
          }}
          {...fieldProps}
          {...otherProps}
          sx={styles}
        />
      }
    />
  );
};

FormikCheckbox.propTypes = {
  field: PropTypes.object.isRequired,
  form: PropTypes.object.isRequired,
  label: PropTypes.string.isRequired,
  onChange: PropTypes.func,
  styles: PropTypes.object,
};

export default FormikCheckbox;
